import { convertMentionsInElement } from "@/lib/utils/mentionUtils";
import { isSafeUrl } from "@/lib/utils/urlUtils";

const isSafeHref = (href: string | null): boolean => {
  if (!href) return false;
  return isSafeUrl(href);
};

const isSafeImageSrc = (src: string | null): boolean => {
  if (!src) return false;

  if (src.startsWith("data:image/")) {
    return true;
  }

  return isSafeUrl(src);
};

/**
 * HTML要素内の危険なURLをサニタイズする（XSS攻撃対策）
 *
 * この関数は以下の処理を行います：
 * 1. リンク要素（<a>タグ）のサニタイズ
 *    - http/https以外のプロトコル（javascript:, data:, file:など）を持つリンクを除去
 *    - 危険なリンクはテキストのみに置換
 *
 * 2. 画像要素（<img>タグ）のサニタイズ
 *    - http/https/data:image/以外のプロトコルを持つ画像を除去
 *    - 危険な画像は要素ごと削除
 *
 * @param element - サニタイズ対象のHTML要素
 */
const sanitizeElementUrls = (element: HTMLElement): void => {
  // リンク要素のサニタイズ
  const anchors = Array.from(
    element.querySelectorAll<HTMLAnchorElement>("a[href]"),
  );
  anchors.forEach((anchor) => {
    if (!isSafeHref(anchor.getAttribute("href"))) {
      // 危険なリンクはテキストのみに置換（リンク機能を無効化）
      const replacement = anchor.textContent ?? "";
      anchor.replaceWith(replacement);
    }
  });

  // 画像要素のサニタイズ
  const images = Array.from(
    element.querySelectorAll<HTMLImageElement>("img[src]"),
  );
  images.forEach((image) => {
    if (!isSafeImageSrc(image.getAttribute("src"))) {
      // 危険な画像は要素ごと削除
      image.remove();
    }
  });
};

export const convertTextToMarkdown = (
  element: HTMLElement,
  showFormatting: boolean,
): string => {
  const clonedElement = element.cloneNode(true) as HTMLElement;

  // メンション要素をマークダウンリンクに変換
  convertMentionsInElement(clonedElement);

  // URLをサニタイズ
  sanitizeElementUrls(clonedElement);

  if (!showFormatting) {
    return clonedElement.textContent || "";
  }

  // HTML要素をMarkdown記法に変換（画像対応）
  return convertHtmlToMarkdown(clonedElement).trim();
};

/**
 * 画像要素をMarkdown形式に変換
 */
const convertImageToMarkdown = (img: HTMLImageElement): string => {
  const src = img.src;
  const alt = img.alt || "image";

  // Base64画像の場合はそのまま使用
  if (src.startsWith("data:image/")) {
    return `![${alt}](${src})`;
  }

  // 通常のURL画像の場合
  return `![${alt}](${src})`;
};

/**
 * Check if formatting element is effectively empty (only contains br, whitespace, or other empty elements)
 */
const isEffectivelyEmpty = (element: HTMLElement): boolean => {
  const textContent = element.textContent?.trim() || "";
  if (textContent !== "") return false;

  // Check if it only contains br elements or other empty formatting elements
  const children = Array.from(element.children);
  return children.every(
    (child) =>
      child.tagName.toLowerCase() === "br" ||
      (child.tagName.toLowerCase() === "strong" &&
        isEffectivelyEmpty(child as HTMLElement)) ||
      (child.tagName.toLowerCase() === "b" &&
        isEffectivelyEmpty(child as HTMLElement)) ||
      (child.tagName.toLowerCase() === "em" &&
        isEffectivelyEmpty(child as HTMLElement)) ||
      (child.tagName.toLowerCase() === "i" &&
        isEffectivelyEmpty(child as HTMLElement)) ||
      (child.tagName.toLowerCase() === "code" &&
        isEffectivelyEmpty(child as HTMLElement)),
  );
};

/**
 * Convert formatting elements (strong/b, em/i, code) to markdown with proper whitespace handling
 */
const convertFormattingToMarkdown = (
  element: HTMLElement,
  content: string,
  marker: string,
): string => {
  if (isEffectivelyEmpty(element)) {
    return content; // This will preserve br as \n, etc.
  } else {
    // Move leading/trailing whitespace outside of the markers
    const leading = content.match(/^\s+/)?.[0] || "";
    const trailing = content.match(/\s+$/)?.[0] || "";
    const inner = content.slice(
      leading.length,
      content.length - trailing.length,
    );
    if (inner.trim() === "") {
      // Avoid emitting empty markers
      return content;
    } else {
      return `${leading}${marker}${inner}${marker}${trailing}`;
    }
  }
};

/**
 * HTML要素をMarkdown記法に変換する（画像対応版）
 * - <div>/<p>/<blockquote>/<pre> はブロック扱いとして、前後に必要な改行を付与
 * - 先頭がテキストで、次にブロックが続く場合でも間に改行を挟む
 */
export const convertHtmlToMarkdown = (element: HTMLElement): string => {
  let result = "";

  for (const node of Array.from(element.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent || "";
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tagName = el.tagName.toLowerCase();

      // 画像要素の処理
      if (tagName === "img") {
        const img = el as HTMLImageElement;
        result += convertImageToMarkdown(img);
        continue;
      }

      // Check if formatting element is effectively empty (only contains br, whitespace, or other empty elements)
      const content = convertHtmlToMarkdown(el);

      const ensureNewlineBeforeBlock = () => {
        if (result && !result.endsWith("\n")) {
          result += "\n";
        }
      };

      switch (tagName) {
        case "strong":
        case "b":
          result += convertFormattingToMarkdown(el, content, "**");
          break;
        case "em":
        case "i":
          result += convertFormattingToMarkdown(el, content, "*");
          break;
        case "a": {
          const href = el.getAttribute("href");
          if (!href) {
            result += content;
            break;
          }

          const linkText = content || el.textContent || href;
          const escapedText = linkText
            .replace(/\[/g, "\\[")
            .replace(/\]/g, "\\]");
          const escapedUrl = href.replace(/\)/g, "\\)");
          result += `[${escapedText}](${escapedUrl})`;
          break;
        }
        case "code":
          result += convertFormattingToMarkdown(el, content, "`");
          break;
        case "pre": {
          // ブロック扱い
          ensureNewlineBeforeBlock();
          result += content;
          if (content && !result.endsWith("\n")) {
            result += "\n";
          }
          break;
        }
        case "blockquote": {
          // 改行で分割して各行に > を付ける（ブロック扱い）
          ensureNewlineBeforeBlock();
          const lines = content.split("\n");
          result += lines.map((line) => `> ${line}`).join("\n");
          if (content && !result.endsWith("\n")) {
            result += "\n";
          }
          break;
        }
        case "h1": {
          ensureNewlineBeforeBlock();
          result += `# ${content.trim()}`;
          if (content && !result.endsWith("\n")) {
            result += "\n";
          }
          break;
        }
        case "h2": {
          ensureNewlineBeforeBlock();
          result += `## ${content.trim()}`;
          if (content && !result.endsWith("\n")) {
            result += "\n";
          }
          break;
        }
        case "br":
          result += "\n";
          break;
        case "div":
        case "p": {
          ensureNewlineBeforeBlock();
          // For div/p elements, check if they only contain empty formatting tags
          const divContent = content.trim();
          if (divContent === "") {
            // If div/p is empty or only whitespace, just add a newline
            result += "\n";
          } else {
            result += content;
            // divの後に改行を追加（ブロック要素として扱う）
            if (content && !result.endsWith("\n")) {
              result += "\n";
            }
          }
          break;
        }
        default:
          result += content;
          break;
      }
    }
  }

  return result;
};
